import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Briefcase, Building2, ArrowLeft, ArrowRight, Euro, CheckCircle2, Home, Clock } from "lucide-react";
import WorkLayout from "@/components/WorkLayout";
import { useAuth } from "@/context/AuthContext";
import { isEmployer } from "@/lib/roles";
import api from "@/lib/api";

const money = (j) => {
  const c = j.currency === "EUR" ? "€" : "$";
  const k = (n) => `${c}${Math.round((n || 0) / 1000)}k`;
  return `${k(j.salary_min)}–${k(j.salary_max)}`;
};

export default function WorkJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const appHref = user ? (isEmployer(user.role) ? "/dashboard" : "/jobs") : "/register";
  const ctaLabel = user ? "Apply now" : "Apply — create free profile";

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(null)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return <WorkLayout title="Loading job… | WorkOnIT" canonicalPath={`/work/job/${id}`}>
      <div className="max-w-3xl mx-auto px-5 py-24 text-center text-slate-400">Loading job…</div>
    </WorkLayout>;
  }
  if (!job) {
    return <WorkLayout title="Job not found | WorkOnIT" canonicalPath={`/work/job/${id}`}>
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <p className="text-slate-300">This job is no longer available.</p>
        <Link to="/work/jobs" className="mt-4 inline-flex items-center gap-2 text-teal-300"><ArrowLeft size={15} /> Browse all jobs</Link>
      </div>
    </WorkLayout>;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const isEUR = job.currency === "EUR";
  const jobPosting = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: `<p>${(job.description || "").replace(/\n/g, "<br/>")}</p>`,
    datePosted: job.posted_at,
    employmentType: (job.job_type || "").toUpperCase().replace("-", "_"),
    hiringOrganization: { "@type": "Organization", name: job.company },
    jobLocation: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: job.location, addressCountry: job.location?.includes("Netherlands") ? "NL" : undefined },
    },
    baseSalary: {
      "@type": "MonetaryAmount", currency: isEUR ? "EUR" : "USD",
      value: { "@type": "QuantitativeValue", minValue: job.salary_min, maxValue: job.salary_max, unitText: "YEAR" },
    },
    directApply: true,
    url: `${origin}/work/job/${job.id}`,
  };

  return (
    <WorkLayout
      title={`${job.title} at ${job.company} — ${job.location} | WorkOnIT`}
      description={`${job.title} job at ${job.company} in ${job.location}. ${job.pay_note || money(job)}. Apply directly on WorkOnIT.`}
      canonicalPath={`/work/job/${job.id}`} image={job.logo || undefined} jsonLd={jobPosting}>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <button onClick={() => navigate(-1)} data-testid="job-back" className="text-sm text-slate-400 hover:text-teal-300 inline-flex items-center gap-1.5 mb-6"><ArrowLeft size={15} /> Back to jobs</button>

        <div className="rounded-3xl border border-white/10 bg-slate-900/50 overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-white/10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 data-testid="job-title" className="font-display font-extrabold text-3xl text-slate-100">{job.title}</h1>
                <div className="mt-1.5 text-slate-400 flex items-center gap-1.5"><Building2 size={15} /> {job.company}</div>
              </div>
              {job.urgent && <span className="shrink-0 text-[10px] font-mono uppercase tracking-wide text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-2 py-1">Urgent</span>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><MapPin size={15} className="text-teal-400" /> {job.location}</span>
              <span className="flex items-center gap-1.5"><Briefcase size={15} className="text-teal-400" /> {job.job_type} · {job.work_mode}</span>
              {job.posted_at && <span className="flex items-center gap-1.5"><Clock size={15} className="text-teal-400" /> Posted recently</span>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span data-testid="job-salary" className="inline-flex items-center gap-2 rounded-xl bg-teal-500/10 border border-teal-500/25 px-3.5 py-2 text-teal-200">
                <Euro size={16} />
                <span className="font-mono font-semibold">{job.hourly_from ? `€${job.hourly_from.toFixed(2)}–€${job.hourly_to.toFixed(2)}/hr` : money(job)}</span>
                <span className="text-xs text-teal-300/80 ml-1">{job.hourly_from ? `≈ ${money(job)}/yr` : "per year"}</span>
              </span>
              {job.housing && <span data-testid="job-housing-badge" className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 px-3 py-2 text-indigo-200 text-sm"><Home size={15} /> Housing & transport</span>}
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <p data-testid="job-description" className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{job.description}</p>
            {job.benefits?.length > 0 && (
              <div className="mt-6">
                <h2 className="font-display font-semibold text-slate-200 mb-2">Pay &amp; benefits</h2>
                <ul className="space-y-1.5">{job.benefits.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle2 size={16} className="text-teal-400 mt-0.5 shrink-0" /> {b}</li>)}</ul>
              </div>
            )}
            {job.skills?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-1.5">
                {job.skills.map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800/80 text-cyan-300 border border-cyan-500/20">{s}</span>)}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between bg-slate-950/40">
            <span className="text-xs text-slate-500">Netherlands minimum from ≈ €15/hour + allowances. Pay varies by role & employer.</span>
            <Link to={appHref} data-testid="job-apply" className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">{ctaLabel} <ArrowRight size={16} /></Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/work/jobs" className="text-sm text-teal-300 inline-flex items-center gap-1.5">Browse more jobs <ArrowRight size={14} /></Link>
        </div>
      </div>
    </WorkLayout>
  );
}
